/**
 * shop.ts — shop catalog + purchase + inventory + feed-pet
 * (PR-15 split of api/client.ts).
 *
 * Owns the kid-facing shopping endpoints and the `feed` action that
 * consumes an inventory item against the active pet. Also defines the
 * shared `ShopItem` / `InventoryItem` shapes that lots of UI components
 * import directly.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Used by ShopPage, ItemShopSection, BuildingInteriorPage,
 *     HomePage feed gestures, HomeOverlays, NicknameSetupModal.
 *   - Backend: backend/src/routes/shop.js.
 *
 * 关键约束：
 *   - feedPet returns a bond_gain + wish payload — callers should pass
 *     these through to the bond UI + wish bubble so the kid sees the
 *     reward animation immediately.
 *   - The WishCheckResponse type lives here (not pets.ts) because it
 *     is also returned by purchaseItem; keeping it co-located with the
 *     shop side avoids a cross-file import cycle with pets.ts.
 */
import { call } from './_core';
// Type-only imports — see pets.ts header for the cycle note.
import type { BondGainResponse, BondLevelUp } from './pets';
import type { RewardBlocked } from './games';

export interface ShopItem {
  id: number;
  category: 'food' | 'toy' | 'decor_wall' | 'decor_floor' | 'decor_furniture' | 'decor_plant' | 'decor_scene' | 'clothing';
  code: string;
  display_name: string;
  description: string | null;
  price: number;
  unlock_level: number;
  species_affinity: string | null;
  effects: string | null;
  sprite_key: string | null;
}

export interface InventoryItem extends ShopItem {
  item_id: number;
  quantity: number;
}

export interface WishCheckResponse {
  ok: true;
  fulfilled: boolean;
  reason?: string;
  wish?: { id: number; text_zh: string; text_en: string };
  reward?: {
    bond_xp: number;
    bond_level: number;
    bond_level_up: BondLevelUp | null;
    sen: number;
    blocked?: RewardBlocked | null;
  };
  pet?: { id: number; nickname: string };
}

export const shopApi = {
  listShopItems(category?: string) {
    const qs = category ? `?category=${encodeURIComponent(category)}` : '';
    return call<{ items: ShopItem[] }>(`/api/v1/child/shop/items${qs}`);
  },
  purchaseItem(item_id: number, quantity = 1) {
    return call<{
      ok: true;
      item: { id: number; display_name: string };
      quantity_bought: number; cost: number; balance_after: number;
      wish?: WishCheckResponse | null;
    }>('/api/v1/child/shop/purchase', {
      method: 'POST', body: JSON.stringify({ item_id, quantity }),
    });
  },
  listInventory() {
    return call<{ items: InventoryItem[] }>('/api/v1/child/inventory');
  },
  feedPet(item_id: number) {
    return call<{
      ok: true;
      pet_state: { hunger: number; happiness: number; level: number; exp: number; [k: string]: unknown };
      used: { item_id: number; display_name: string };
      bonus: boolean;
      bond_gain?: BondGainResponse | null;
      wish?: WishCheckResponse | null;
    }>('/api/v1/child/shop/feed', {
      method: 'POST', body: JSON.stringify({ item_id }),
    });
  },
};
